const YOUTUBE_HOSTS = new Set(["youtube.com", "www.youtube.com", "m.youtube.com", "music.youtube.com", "youtu.be", "www.youtu.be"]);
const VIMEO_HOSTS = new Set(["vimeo.com", "www.vimeo.com", "player.vimeo.com"]);
const DAILYMOTION_HOSTS = new Set(["dailymotion.com", "www.dailymotion.com", "dai.ly"]);
const TWITCH_HOSTS = new Set(["twitch.tv", "www.twitch.tv", "m.twitch.tv", "clips.twitch.tv"]);

const YOUTUBE_ID = /^[\w-]{11}$/;
const DAILYMOTION_ID = /^[a-z0-9]{5,12}$/i;
const TWITCH_SLUG = /^[\w-]{3,100}$/;
const WISTIA_ID = /^[a-z0-9]{8,12}$/i;
const PEERTUBE_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const PEERTUBE_SHORT = /^[1-9A-HJ-NP-Za-km-z]{20,24}$/;

const segments = (url) => url.pathname.split("/").filter(Boolean);

const youtube = (url) => {
  if (!YOUTUBE_HOSTS.has(url.hostname)) {
    return null;
  }

  let id = null;

  if (url.hostname.endsWith("youtu.be")) {
    id = segments(url)[0];
  } else if (url.pathname === "/watch") {
    id = url.searchParams.get("v");
  } else {
    const [kind, candidate] = segments(url);

    if (["shorts", "embed", "live", "v"].includes(kind)) {
      id = candidate;
    }
  }

  if (!id || !YOUTUBE_ID.test(id)) {
    return null;
  }

  return {
    provider: "youtube",
    name: "YouTube",
    embedUrl: () => `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0`,
  };
};

const vimeo = (url) => {
  if (!VIMEO_HOSTS.has(url.hostname)) {
    return null;
  }

  const parts = segments(url);
  const index = parts.findIndex(part => /^\d{5,}$/.test(part));

  if (index === -1) {
    return null;
  }

  // Only plain video paths: /ID, /ID/HASH, /channels/x/ID, /groups/x/videos/ID, /video/ID
  const before = parts.slice(0, index).join("/");

  if (!(before === "" || before === "video" || /^channels\/[^/]+$/.test(before) || /^groups\/[^/]+\/videos$/.test(before))) {
    return null;
  }

  const id = parts[index];
  const hash = /^[0-9a-f]{8,}$/i.test(parts[index + 1] || "") ? parts[index + 1] : url.searchParams.get("h");

  return {
    provider: "vimeo",
    name: "Vimeo",
    embedUrl: () => `https://player.vimeo.com/video/${id}?autoplay=1&dnt=1${hash ? `&h=${hash}` : ""}`,
  };
};

const dailymotion = (url) => {
  if (!DAILYMOTION_HOSTS.has(url.hostname)) {
    return null;
  }

  const parts = segments(url);
  let id = null;

  if (url.hostname === "dai.ly") {
    id = parts[0];
  } else if (parts[0] === "video") {
    id = parts[1];
  } else if (parts[0] === "embed" && parts[1] === "video") {
    id = parts[2];
  }

  // Old style ids look like "x8abc12_title"; only the part before "_" is the id.
  id = id?.split("_")[0];

  if (!id || !DAILYMOTION_ID.test(id)) {
    return null;
  }

  return {
    provider: "dailymotion",
    name: "Dailymotion",
    embedUrl: () => `https://www.dailymotion.com/embed/video/${id}?autoplay=1`,
  };
};

const twitch = (url) => {
  if (!TWITCH_HOSTS.has(url.hostname)) {
    return null;
  }

  const parts = segments(url);
  let kind = null;
  let id = null;

  if (url.hostname === "clips.twitch.tv") {
    kind = "clip";
    id = parts[0] === "embed" ? url.searchParams.get("clip") : parts[0];
  } else if (parts[0] === "videos" && /^\d+$/.test(parts[1] || "")) {
    kind = "video";
    id = parts[1];
  } else if (parts[1] === "clip") {
    kind = "clip";
    id = parts[2];
  }

  if (!kind || !id || (kind === "clip" && !TWITCH_SLUG.test(id))) {
    return null;
  }

  return {
    provider: "twitch",
    name: "Twitch",
    embedUrl: () => {
      const parent = encodeURIComponent(window.location.hostname);

      return kind === "video"
        ? `https://player.twitch.tv/?video=v${id}&parent=${parent}&autoplay=true`
        : `https://clips.twitch.tv/embed?clip=${id}&parent=${parent}&autoplay=true`;
    },
  };
};

const wistia = (url) => {
  if (!(url.hostname === "wistia.com" || url.hostname.endsWith(".wistia.com") || url.hostname === "wi.st")) {
    return null;
  }

  const parts = segments(url);
  const id = parts[0] === "medias" ? parts[1] : null;

  if (!id || !WISTIA_ID.test(id)) {
    return null;
  }

  return {
    provider: "wistia",
    name: "Wistia",
    embedUrl: () => `https://fast.wistia.net/embed/iframe/${id}?autoPlay=true`,
  };
};

// PeerTube runs on many instances, so it is recognised by its URL scheme:
// /w/<short id>, /videos/watch/<uuid or short id>, /videos/embed/<id>.
const peertube = (url) => {
  const parts = segments(url);
  let id = null;

  if (parts[0] === "w" && parts.length === 2) {
    id = parts[1];
  } else if (parts[0] === "videos" && ["watch", "embed"].includes(parts[1]) && parts.length === 3) {
    id = parts[2];
  }

  if (!id || !(PEERTUBE_UUID.test(id) || PEERTUBE_SHORT.test(id))) {
    return null;
  }

  return {
    provider: "peertube",
    name: `PeerTube (${url.hostname})`,
    sandbox: "allow-same-origin allow-scripts allow-popups allow-forms",
    embedUrl: () => `${url.origin}/videos/embed/${id}?autoplay=1&warningTitle=0`,
  };
};

const PROVIDERS = [youtube, vimeo, dailymotion, twitch, wistia, peertube];

/**
 * Describes the video behind a URL, or returns null.
 * @param {string} href
 * @returns {{ provider: string, name: string, sandbox?: string, embedUrl: () => string } | null}
 */
export const videoFromUrl = (href) => {
  let url;

  try {
    url = new URL(href);
  } catch {
    return null;
  }

  if (!["http:", "https:"].includes(url.protocol)) {
    return null;
  }

  for (const provider of PROVIDERS) {
    const video = provider(url);

    if (video) {
      return video;
    }
  }

  return null;
};

/**
 * Finds the first embeddable video link in the HTML content of a status.
 * @param {string} html
 */
export const findVideoLink = (html) => {
  if (!html) {
    return null;
  }

  const document = new DOMParser().parseFromString(html, "text/html");

  for (const link of document.querySelectorAll("a[href]")) {
    if (link.classList.contains("mention") || link.classList.contains("hashtag")) {
      continue;
    }

    const video = videoFromUrl(link.href);

    if (video) {
      return { ...video, url: link.href };
    }
  }

  return null;
};
